import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity, AlertCircle, CheckCircle, X } from 'lucide-react';

interface ComponentStatus {
  name: string;
  status: 'operational' | 'degraded_performance' | 'partial_outage' | 'major_outage';
}

interface StatusData {
  overall: 'operational' | 'degraded' | 'outage';
  components: ComponentStatus[];
  lastUpdated: Date;
}

export function SupabaseStatus() {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
    // Refresh every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function fetchStatus() {
    try {
      // Fetch from Supabase status API
      const response = await fetch('https://status.supabase.com/api/v2/status.json');
      const data = await response.json();

      // Get components we care about
      const componentsResponse = await fetch('https://status.supabase.com/api/v2/components.json');
      const componentsData = await componentsResponse.json();

      // Filter for the services we use
      const relevantComponents = componentsData.components.filter((c: any) => 
        c.name.includes('API') || 
        c.name.includes('Database') || 
        c.name.includes('Edge Functions') ||
        c.name.includes('Auth') ||
        c.name.includes('Storage')
      );

      // Determine overall status
      let overallStatus: 'operational' | 'degraded' | 'outage' = 'operational';
      
      if (data.status.indicator === 'major' || data.status.indicator === 'critical') {
        overallStatus = 'outage';
      } else if (data.status.indicator === 'minor') {
        overallStatus = 'degraded';
      }

      setStatus({
        overall: overallStatus,
        components: relevantComponents.map((c: any) => ({
          name: c.name,
          status: c.status
        })),
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error fetching Supabase status:', error);
      // Set unknown status on error
      setStatus({
        overall: 'degraded',
        components: [],
        lastUpdated: new Date()
      });
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (statusType: string) => {
    switch (statusType) {
      case 'operational':
        return 'text-green-500';
      case 'degraded_performance':
      case 'degraded':
        return 'text-yellow-500';
      case 'partial_outage':
      case 'major_outage':
      case 'outage':
        return 'text-red-500';
      default:
        return 'text-slate-400';
    }
  };

  const getStatusIcon = (statusType: string) => {
    switch (statusType) {
      case 'operational':
        return <CheckCircle className="w-3 h-3" />;
      case 'degraded_performance':
      case 'degraded':
        return <AlertCircle className="w-3 h-3" />;
      case 'partial_outage':
      case 'major_outage':
      case 'outage':
        return <AlertCircle className="w-3 h-3" />;
      default:
        return <Activity className="w-3 h-3" />;
    }
  };

  const getStatusText = (statusType: string) => {
    switch (statusType) {
      case 'operational':
        return 'All Systems Operational';
      case 'degraded_performance':
      case 'degraded':
        return 'Degraded Performance';
      case 'partial_outage':
        return 'Partial Outage';
      case 'major_outage':
      case 'outage':
        return 'Service Outage';
      default:
        return 'Unknown Status';
    }
  };

  if (loading || !status) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-800 rounded border border-slate-700">
        <Activity className="w-3 h-3 text-slate-400 animate-pulse" />
        <span className="text-xs text-slate-400">Checking...</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-1.5 px-2 py-1 bg-slate-800 rounded border border-slate-700 hover:bg-slate-700 transition-colors"
        title="Supabase Status"
      >
        <div className={getStatusColor(status.overall)}>
          {getStatusIcon(status.overall)}
        </div>
        <span className={`text-xs font-medium ${getStatusColor(status.overall)}`}>
          STATUS
        </span>
      </button>

      {/* Status Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 999999 }} onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-slate-900">Supabase Status</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Overall Status */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className={getStatusColor(status.overall)}>
                  {getStatusIcon(status.overall)}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">Overall Status</p>
                  <p className={`text-xs font-medium ${getStatusColor(status.overall)}`}>
                    {getStatusText(status.overall)}
                  </p>
                </div>
              </div>

              {/* Component Status */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Services</h4>
                <div className="space-y-2">
                  {status.components.length > 0 ? (
                    status.components.map((component, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm text-slate-700">{component.name}</span>
                        <div className={`flex items-center gap-1 ${getStatusColor(component.status)}`}>
                          {getStatusIcon(component.status)}
                          <span className="text-xs font-medium capitalize">
                            {component.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-2">No component data available</p>
                  )}
                </div>
              </div>

              {/* Last Updated */}
              <div className="text-xs text-slate-500 text-center">
                Last updated: {status.lastUpdated.toLocaleTimeString()}
              </div>

              {/* Link to Status Page */}
              <div className="pt-2 border-t border-slate-200">
                <a
                  href="https://status.supabase.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View Full Status Page →
                </a>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
